import {type ReactNode, useEffect, useRef, useState} from 'react';
import {EyeOff, Send} from 'lucide-react';
import {
  Button,
  Field,
  FieldLabel,
  Kbd,
  NativeSelect,
  NativeSelectOption
} from '@kroll-resolver-crisp/ui-components-react';
import {INDETERMINATE_REASONS, type IndeterminateReason} from '@/api/moderationQueue';

type Props = {
  readonly busy: boolean;
  // Another dialog is open over the page: the shortcuts must not reach through it.
  readonly shortcutsPaused: boolean;
  readonly inline?: boolean;
  // Shown above the decision, so the analyst weighs it next to the buttons that answer it.
  readonly verdict?: ReactNode;
  readonly onClassify: () => void;
  readonly onDismiss: () => void;
  readonly onIndeterminate: (reason: IndeterminateReason) => void;
  readonly onSkip: () => void;
};

const isTyping = (target: EventTarget | null) =>
  target instanceof HTMLElement &&
  (target.isContentEditable || ['INPUT', 'TEXTAREA', 'SELECT'].includes(target.tagName));

// What the analyst decides about the held task. V opens the classify dialog over the whole
// taxonomy, N is not violative, X is can't review (the item can't be judged, so the risk is unknown
// rather than absent) and S skips; the shortcuts are ignored while typing. Mount it with
// key={task.id} so the form starts empty for each task.
export function QueueDecisionPanel({
  busy,
  shortcutsPaused,
  inline = false,
  verdict,
  onClassify,
  onDismiss,
  onIndeterminate,
  onSkip
}: Props) {
  const [indeterminating, setIndeterminating] = useState(false);
  const [indeterminateReason, setIndeterminateReason] = useState<IndeterminateReason>('removed');

  // Read through a ref so the one window listener always sees the current props.
  const latest = useRef({busy, shortcutsPaused, indeterminating, onClassify, onDismiss, onSkip});
  useEffect(() => {
    latest.current = {busy, shortcutsPaused, indeterminating, onClassify, onDismiss, onSkip};
  });

  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      const {current} = latest;
      if (
        event.repeat ||
        event.metaKey ||
        event.ctrlKey ||
        event.altKey ||
        isTyping(event.target) ||
        current.shortcutsPaused ||
        current.busy ||
        current.indeterminating
      ) {
        return;
      }

      switch (event.key.toLowerCase()) {
        case 'v': {
          current.onClassify();
          break;
        }

        case 'n': {
          current.onDismiss();
          break;
        }

        case 'x': {
          setIndeterminating(true);
          break;
        }

        case 's': {
          current.onSkip();
          break;
        }

        default: {
          return;
        }
      }

      event.preventDefault();
    };

    globalThis.addEventListener('keydown', onKeyDown);
    return () => {
      globalThis.removeEventListener('keydown', onKeyDown);
    };
  }, []);

  return (
    <aside
      className={
        inline
          ? 'border-border bg-background flex max-h-[55%] shrink-0 flex-col border-t'
          : 'border-border bg-background flex min-h-0 w-85 shrink-0 flex-col border-l'
      }>
      {verdict || indeterminating ? (
        <div className="flex min-h-0 flex-1 flex-col gap-4 overflow-auto p-4">
          {verdict}
          {indeterminating ? (
            <>
              <div className="flex flex-col gap-1">
                <span className="text-sm font-medium">Can’t review</span>
                <span className="text-muted-foreground text-xs/relaxed">
                  Closes this task without a decision. The risk is recorded as unknown, not as no
                  risk.
                </span>
              </div>
              <Field>
                <FieldLabel htmlFor="queue-indeterminate-reason">Why?</FieldLabel>
                <NativeSelect
                  id="queue-indeterminate-reason"
                  value={indeterminateReason}
                  aria-label="Reason"
                  onChange={(event) => {
                    setIndeterminateReason(event.target.value as IndeterminateReason);
                  }}>
                  {INDETERMINATE_REASONS.map((option) => (
                    <NativeSelectOption key={option.value} value={option.value}>
                      {option.label}
                    </NativeSelectOption>
                  ))}
                </NativeSelect>
              </Field>
            </>
          ) : null}
        </div>
      ) : (
        <div className="flex-1" />
      )}

      <div className="border-border bg-card flex flex-col gap-2 border-t p-4">
        {indeterminating ? (
          <>
            <Button
              type="button"
              disabled={busy}
              onClick={() => {
                onIndeterminate(indeterminateReason);
              }}>
              Close as can’t review
            </Button>
            <Button
              type="button"
              variant="ghost"
              onClick={() => {
                setIndeterminating(false);
              }}>
              Cancel
            </Button>
          </>
        ) : (
          <>
            <Button type="button" disabled={busy} onClick={onClassify}>
              <Send />
              Classify
              <Kbd className="ml-auto">V</Kbd>
            </Button>
            <div className="grid grid-cols-2 gap-2">
              <Button
                type="button"
                variant="secondary"
                disabled={busy}
                title="Recorded decision: not violative for this vertical"
                onClick={onDismiss}>
                Not violative
                <Kbd className="ml-auto">N</Kbd>
              </Button>
              <Button
                type="button"
                variant="ghost"
                disabled={busy}
                title="Return to the pool for someone else"
                onClick={onSkip}>
                Skip
                <Kbd className="ml-auto">S</Kbd>
              </Button>
            </div>
            <Button
              type="button"
              variant="secondary"
              disabled={busy}
              title="The item can't be judged (removed, broken or unclear): recorded as unknown, not as no risk"
              onClick={() => {
                setIndeterminating(true);
              }}>
              <EyeOff />
              Can’t review
              <Kbd className="ml-auto">X</Kbd>
            </Button>
          </>
        )}
      </div>
    </aside>
  );
}
